import React from 'react';
import { trackEvent, BOOKING_SCREENING_URL, EMAIL_ADDRESS } from '../lib/tracking';
import { optimizeImage } from '../lib/image';

export default function ConflictManagement() {
  return (
    <div className="bg-[#ECEEF1]">
      {/* Hero */}
      <section className="py-16 sm:py-24 bg-white border-b border-[#D9DDE2]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7 space-y-6">
              <span className="text-xs uppercase tracking-widest font-semibold text-[#5A232E] block">
                Erhverv · Dialog & Samarbejde
              </span>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#5A232E] tracking-tight leading-[1.12]">
                Konflikthåndtering og mægling
              </h1>
              <p className="text-base sm:text-lg text-[#6c7480] leading-relaxed">
                Konflikter er en naturlig del af enhver relation – på arbejdspladsen, i teams eller i ledelsen. Men når uenigheder vokser, kan det skabe mistrivsel, dårlig stemning og blokere for samarbejdet og organisationens mål.
              </p>
              <div className="flex flex-wrap gap-4 pt-2">
                <a
                  href={BOOKING_SCREENING_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackEvent('cta_booking_click', { location: 'conflict_management_hero' })}
                  className="inline-flex items-center gap-2 px-7 py-3.5 rounded-xl bg-[#5A232E] hover:bg-[#431722] text-white font-semibold text-sm shadow-md transition-all no-break whitespace-nowrap"
                >
                  <i className="fa-solid fa-calendar-check" aria-hidden="true" />
                  <span>Book en gratis afklaringssamtale</span>
                </a>
                <a
                  href={`mailto:${EMAIL_ADDRESS}?subject=Forespørgsel på konflikthåndtering eller mægling`}
                  className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl bg-[#ECEEF1] text-[#5A232E] font-medium text-sm border border-[#D9DDE2] hover:bg-[#D9DDE2] transition-colors no-break whitespace-nowrap"
                >
                  <i className="fa-solid fa-envelope" aria-hidden="true" />
                  <span>Kontakt Kim for en dialog</span>
                </a>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="rounded-3xl overflow-hidden shadow-2xl border-4 border-white bg-[#D9DDE2]">
                <img
                  src={optimizeImage('kim-portrait-wide.webp', 800)}
                  alt="Kim Palasz - Konflikthåndtering og mægling"
                  className="w-full h-auto object-cover max-h-[440px]"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mæglingsprocessen */}
      <section className="py-20 sm:py-24 bg-[#ECEEF1] border-b border-[#D9DDE2]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-16">
          <div className="max-w-3xl">
            <span className="text-xs uppercase tracking-widest font-semibold text-[#5A232E] block mb-2">
              Struktureret Forløb
            </span>
            <h2 className="font-display text-3xl sm:text-4xl text-[#5A232E]">
              Professionel mægling med fokus på holdbare løsninger
            </h2>
            <p className="text-sm sm:text-base text-[#6c7480] mt-3 leading-relaxed">
              Som neutral tredjepart hjælper jeg som mægler med at afdække kerneproblemerne, skabe en tryg ramme for dialogen og lede parterne frem mod holdbare win-win-løsninger, hvor alle parter føler sig hørt og respekteret.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-3xl border border-[#D9DDE2] shadow-xs space-y-4">
              <div className="w-12 h-12 rounded-2xl bg-[#5A232E] text-white flex items-center justify-center font-display font-bold text-lg">
                1
              </div>
              <h3 className="font-display text-xl text-[#5A232E]">Forberedelsesfase</h3>
              <p className="text-xs sm:text-sm text-[#6c7480] leading-relaxed">
                Individuelle, fortrolige forsamtaler med hver part for at afdække perspektiver, behov og personlige bekymringer forud for fællesmødet.
              </p>
            </div>

            <div className="bg-white p-8 rounded-3xl border border-[#D9DDE2] shadow-xs space-y-4">
              <div className="w-12 h-12 rounded-2xl bg-[#5A232E] text-white flex items-center justify-center font-display font-bold text-lg">
                2
              </div>
              <h3 className="font-display text-xl text-[#5A232E]">Faciliteringsfase</h3>
              <p className="text-xs sm:text-sm text-[#6c7480] leading-relaxed">
                Strukturerede fælles mæglermøder, hvor jeg sikrer et trygt og konstruktivt rum. Vi arbejder fra positioner til reelle interesser og fremadrettede aftaler.
              </p>
            </div>

            <div className="bg-white p-8 rounded-3xl border border-[#D9DDE2] shadow-xs space-y-4">
              <div className="w-12 h-12 rounded-2xl bg-[#5A232E] text-white flex items-center justify-center font-display font-bold text-lg">
                3
              </div>
              <h3 className="font-display text-xl text-[#5A232E]">Opfølgningsfase</h3>
              <p className="text-xs sm:text-sm text-[#6c7480] leading-relaxed">
                Opfølgende statusmøde for at evaluere de indgåede aftaler, justere samarbejdsformerne og sikre, at forandringen er varig og bæredygtig.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Afsluttende CTA */}
      <section className="py-20 bg-white border-b border-[#D9DDE2]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-6">
          <h2 className="font-display text-3xl sm:text-4xl text-[#5A232E]">
            Lad os tale om jeres udfordring
          </h2>
          <p className="text-sm sm:text-base text-[#6c7480] max-w-xl mx-auto leading-relaxed">
            Tidlig indgriben ved konflikter sparer både energi, relationer og ressourcer. Tag en uforpligtende samtale for at høre om mulighederne for jeres organisation eller team.
          </p>
          <div className="flex flex-wrap justify-center gap-4 pt-2">
            <a
              href={BOOKING_SCREENING_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-[#5A232E] hover:bg-[#431722] text-white font-semibold text-sm shadow-md transition-all no-break whitespace-nowrap"
            >
              <i className="fa-solid fa-calendar-check" aria-hidden="true" />
              <span>Book en gratis afklaringssamtale</span>
            </a>
            <a
              href={`mailto:${EMAIL_ADDRESS}?subject=Forespørgsel om konflikthåndtering`}
              className="inline-flex items-center gap-2 px-7 py-4 rounded-xl bg-[#ECEEF1] hover:bg-[#D9DDE2] text-[#5A232E] font-medium text-sm border border-[#D9DDE2] transition-colors no-break whitespace-nowrap"
            >
              <i className="fa-solid fa-envelope" aria-hidden="true" />
              <span>Send en e-mail</span>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
